/**
 * useIdleTimeout Hook
 *
 * Forces logout after a period of no user interaction (mouse, keyboard,
 * touch, scroll), even while the tab stays open and tokens keep refreshing
 * on their own timer. Without this, a session with a valid refresh token
 * never ends just because the screen is inactive.
 */
import { useEffect, useRef } from 'react';
import { CONFIG } from '../config';

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'mousemove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
];

// How often to check elapsed idle time against the threshold.
const CHECK_INTERVAL_MS = 15_000;

// Activity listeners fire very frequently (mousemove especially); only
// update the last-activity timestamp at most this often to avoid excessive
// writes on every tick of the check interval.
const ACTIVITY_THROTTLE_MS = 5_000;

export function useIdleTimeout(enabled: boolean, onIdle: () => void): void {
  const lastActivityRef = useRef(0);
  const onIdleRef = useRef(onIdle);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!enabled || CONFIG.IDLE_TIMEOUT_MINUTES <= 0) return;

    const timeoutMs = CONFIG.IDLE_TIMEOUT_MINUTES * 60 * 1000;
    lastActivityRef.current = Date.now();

    const markActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current >= ACTIVITY_THROTTLE_MS) {
        lastActivityRef.current = now;
      }
    };

    // Coming back to a tab after it was hidden (screen lock, app switch)
    // should be treated as an immediate idle check rather than waiting for
    // the next interval tick.
    const checkIdle = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityRef.current >= timeoutMs) {
        onIdleRef.current();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const interval = setInterval(checkIdle, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [enabled]);
}
